import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";
import { PhoneNumber, isValidE164 } from "@/models/PhoneNumber";
import {
  createPhoneNumber,
  updatePhoneNumber,
} from "@/services/phoneNumberService";
import { useOrganization } from "../../App";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface PhoneNumberFormProps {
  phoneNumber?: PhoneNumber;
  onSuccess: () => void;
}

const formSchema = z
  .object({
    phone_number: z
      .string()
      .min(1, "Phone number is required")
      .refine(isValidE164, {
        message: "Phone number must be in E.164 format (e.g., +12125551234)",
      }),
    type: z.enum(["inbound", "outbound"]),
    termination_sip_domain: z
      .string()
      .regex(
        /^(?:[a-z0-9](?:[a-z0-9-_.]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/,
        "Must be a valid domain name",
      )
      .optional()
      .or(z.literal(""))
      .transform((val) => (val === "" ? undefined : val)),
    auth_username: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((val) => (val === "" ? undefined : val)),
    auth_password: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((val) => (val === "" ? undefined : val)),
    is_default: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // Additional validation for outbound type
    if (data.type === "outbound" && !data.termination_sip_domain) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Termination SIP domain is required for outbound phone numbers",
        path: ["termination_sip_domain"],
      });
    }

    // For Plivo SIP trunk, we need username and password for authentication
    if (
      data.type === "outbound" &&
      data.termination_sip_domain &&
      (!data.auth_username || !data.auth_password)
    ) {
      if (!data.auth_username) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "SIP username is required for Plivo SIP trunk",
          path: ["auth_username"],
        });
      }
      if (!data.auth_password) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "SIP password is required for Plivo SIP trunk",
          path: ["auth_password"],
        });
      }
    }
  });

type FormValues = z.infer<typeof formSchema>;

const PhoneNumberForm: React.FC<PhoneNumberFormProps> = ({
  phoneNumber,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone_number: phoneNumber?.phone_number || "",
      type: phoneNumber?.type || "inbound",
      termination_sip_domain: phoneNumber?.termination_sip_domain || "",
      auth_username: phoneNumber?.auth_username || "",
      auth_password: phoneNumber?.auth_password || "",
      is_default: phoneNumber?.is_default || false,
    },
    mode: "onBlur",
  });

  const watchType = form.watch("type");

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setServerError(null);
    try {
      if (phoneNumber) {
        await updatePhoneNumber(phoneNumber.id, data);
        toast({
          title: "Success",
          description: "Phone number updated successfully",
        });
        onSuccess();
      } else {
        await createPhoneNumber(data, currentOrganization?.id);
        toast({
          title: "Success",
          description: "Phone number added successfully",
        });
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error saving phone number:", error);

      // Check if it's a validation error from the server
      if (error?.message?.includes("termination_sip_domain")) {
        form.setError("termination_sip_domain", {
          type: "server",
          message: "Invalid termination SIP domain",
        });
      } else {
        // General error
        setServerError(
          error?.message || "Failed to save phone number. Please try again.",
        );
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save phone number. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {serverError && (
          <Alert variant="destructive" className="mb-6 rounded-none">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Phone Number <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="+12125551234"
                  {...field}
                  className={
                    form.formState.errors.phone_number
                      ? "border-destructive"
                      : ""
                  }
                  onBlur={(e) => {
                    field.onBlur();
                    // Automatically format the phone number if needed
                    let value = e.target.value.trim();
                    // Remove any spaces or dashes
                    value = value.replace(/[\s-]/g, "");
                    if (value && !value.startsWith("+")) {
                      field.onChange(`+${value}`);
                    } else {
                      field.onChange(value);
                    }
                  }}
                />
              </FormControl>
              <FormDescription>
                Enter the phone number in E.164 format (e.g., +12125551234)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Phone Number Type</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="inbound" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Inbound (Receive calls)
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="outbound" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Outbound (Make calls)
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchType === "outbound" && (
          <>
            <FormField
              control={form.control}
              name="termination_sip_domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Termination SIP Domain{" "}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="sip.example.com"
                      {...field}
                      className={
                        form.formState.errors.termination_sip_domain
                          ? "border-destructive"
                          : ""
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    The domain name for SIP termination for outbound calls
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4 bg-muted/40 p-4 rounded-none border border-border">
              <div className="text-sm font-medium">
                Plivo SIP Trunk Configuration
              </div>

              <FormField
                control={form.control}
                name="auth_username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      SIP Username <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="plivo_username"
                        {...field}
                        className={
                          form.formState.errors.auth_username
                            ? "border-destructive"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Username for Plivo SIP authentication
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="auth_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      SIP Password <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="password"
                        {...field}
                        className={
                          form.formState.errors.auth_password
                            ? "border-destructive"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Password for Plivo SIP authentication
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_default"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-none border border-border p-4">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4 mr-2"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Set as default outbound number</FormLabel>
                      <FormDescription>
                        This number will be used for outbound calls when no
                        specific number is selected.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </>
        )}

        <div className="flex justify-between space-x-2">
          <div className="text-sm text-muted-foreground">
            <span className="text-destructive">*</span> Required fields
          </div>
          <Button
            type="submit"
            variant="primaryOutline"
            className="rounded-none"
            isLoading={isSubmitting}
            loadingText={`${phoneNumber ? "Updating" : "Adding"} Phone Number...`}
          >
            {phoneNumber ? "Update" : "Add"} Phone Number
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PhoneNumberForm;
